import { Modal } from "../../utils/Modal";
import { Form } from "../../core/Form";
import { initAdminFormControls } from "../global/initAdminFormControls";
import { initPageBlockBuilder } from "./PageBlockBuilder";

/**
 * 페이지(메뉴) 추가·수정 모달.
 *
 * 게시판 캘린더(AdminPostCalendar.openCalModal)와 동일 패턴:
 *   GET form-fragment(-edit) → 레이아웃 없는 폼 HTML → 모달 주입 →
 *   initAdminFormControls(scope) + initPageBlockBuilder() 재초기화 →
 *   new Form(...) 제출 → 성공 시 close + reload. (수정 모달엔 삭제 버튼)
 *
 * 트리거: [data-page-modal] (data-url=프래그먼트, data-title). a[href] 는 JS 미동작 시 폴백.
 */

function escHtml(str: string): string {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
}

let pageModalOpen = false;

export async function openPageModal(url: string, title: string): Promise<void> {
    if (pageModalOpen) return;
    pageModalOpen = true;

    let html: string;
    try {
        const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
        if (!res.ok) { pageModalOpen = false; await Modal.alert("폼을 불러오지 못했습니다."); return; }
        html = await res.text();
    } catch {
        pageModalOpen = false;
        await Modal.alert("네트워크 오류가 발생했습니다.");
        return;
    }

    const root = document.getElementById("app-modal-root") ?? (() => {
        const el = document.createElement("div");
        el.id = "app-modal-root";
        document.body.appendChild(el);
        return el;
    })();

    const backdrop = document.createElement("div");
    backdrop.className = "app-modal-backdrop";

    const box = document.createElement("div");
    box.className = "app-modal-box app-modal-box--wide site-page-modal";
    box.innerHTML = `
        <div class="app-modal-header">
            <h2 class="app-modal-title">${escHtml(title)}</h2>
            <button type="button" class="app-modal-close" data-page-modal-close aria-label="닫기">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
        </div>
        <div class="app-modal-body app-modal-body--scroll site-page-modal__body" data-lenis-prevent></div>
        <div class="app-modal-footer site-page-modal__footer">
            <button type="button" class="app-modal-btn" data-page-modal-close>취소</button>
            <button type="submit" form="page-modal-form" class="app-modal-btn app-modal-btn--primary">저장</button>
        </div>
    `;

    const body = box.querySelector<HTMLElement>(".site-page-modal__body")!;
    body.innerHTML = html;

    // CSRF 동기화 — 프래그먼트의 최신 토큰을 meta/_token 에 반영(삭제·업로드 AJAX 용).
    const freshToken = body.querySelector<HTMLInputElement>('input[name="_token"]')?.value;
    if (freshToken) {
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.setAttribute("content", freshToken);
        document.querySelectorAll<HTMLInputElement>('input[name="_token"]').forEach((i) => { i.value = freshToken; });
    }

    backdrop.appendChild(box);
    root.appendChild(backdrop);

    // 폼 컨트롤(언어탭·리치에디터·업로더·셀렉트) + 블록 빌더 초기화.
    initAdminFormControls(body);
    initPageBlockBuilder(); // 주입된 [data-page-block-builder] 를 document 스캔으로 init (멱등)

    const close = (): void => { backdrop.remove(); pageModalOpen = false; };
    box.querySelectorAll("[data-page-modal-close]").forEach((btn) => btn.addEventListener("click", close));
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    box.addEventListener("click", (e) => e.stopPropagation());

    const form = body.querySelector<HTMLFormElement>("form[data-page-modal-form]");
    if (form) {
        new Form(form, {
            onSuccess() { close(); location.reload(); },
            onFail(msg) { if (msg) void Modal.alert(msg); },
            requestFailedMessage: "요청에 실패했습니다.",
            invalidResponseMessage: "응답을 처리할 수 없습니다.",
        }).init();
    }

    const delBtn = body.querySelector<HTMLButtonElement>("[data-page-modal-delete]");
    if (delBtn) {
        delBtn.addEventListener("click", async () => {
            const confirmed = await Modal.confirm(delBtn.dataset.confirm ?? "삭제할까요?");
            if (!confirmed) return;
            const actionUrl = delBtn.dataset.action ?? "";
            if (!actionUrl) return;
            const csrf =
                document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
                body.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
            try {
                const res = await fetch(actionUrl, {
                    method: "POST",
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-TOKEN": csrf,
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: `_method=DELETE&_token=${encodeURIComponent(csrf)}`,
                });
                if (res.ok) { close(); location.reload(); }
                else { await Modal.alert("삭제에 실패했습니다."); }
            } catch {
                await Modal.alert("네트워크 오류가 발생했습니다.");
            }
        });
    }
}

interface ModalBoundWindow extends Window {
    _pageFormModalBound?: boolean;
}

export function initPageFormModal(): void {
    const w = window as ModalBoundWindow;
    if (w._pageFormModalBound) return;
    w._pageFormModalBound = true;

    document.addEventListener("click", (e) => {
        const trigger = (e.target as HTMLElement).closest<HTMLElement>("[data-page-modal]");
        if (!trigger) return;
        const url = trigger.dataset.url ?? trigger.getAttribute("href") ?? "";
        if (!url) return;
        e.preventDefault();
        void openPageModal(url, trigger.dataset.title ?? "페이지");
    });
}
